/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/EmptyState.tsx
 * PURPOSE: CRM empty list or panel placeholder — centred icon, title, optional copy and action (named region, not live status)
 * DEPENDENCIES: react, flowbite-react
 * PUBLIC_API: EmptyState, EmptyStateProps
 * SIDE_EFFECTS: Invokes action.onClick when the action button is used
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~65/200
 */

import { Button } from 'flowbite-react';
import { useId, type ReactNode } from 'react';

export type EmptyStateProps = {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void; dataTestId?: string };
  className?: string;
};

export function EmptyState({ title, description, icon, action, className = '' }: EmptyStateProps) {
  const titleId = useId();
  return (
    <div
      className={`flex flex-col items-center justify-center px-4 py-12 text-center ${className}`}
      data-testid="crm-empty-state"
      role="region"
      aria-labelledby={titleId}
    >
      {icon ? (
        <div
          className="mb-4 flex h-12 w-12 shrink-0 items-center justify-center text-gray-400 dark:text-gray-500 [&>svg]:h-12 [&>svg]:w-12"
          aria-hidden
        >
          {icon}
        </div>
      ) : null}
      <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
        {title}
      </h3>
      {description ? (
        <p className="mt-2 max-w-md text-sm text-gray-600 dark:text-gray-400">{description}</p>
      ) : null}
      {action ? (
        <Button
          type="button"
          className="mt-6"
          color="blue"
          data-testid={action.dataTestId}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
